import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoSrc from '../assets/VL_Logo.png';

const PUBLIC_LINKS = [
  {
    to: '/about', label: 'About',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  },
  {
    to: '/templates', label: 'Templates',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
  },
  {
    to: '/pricing', label: 'Pricing',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  },
  {
    to: '/faq', label: 'FAQ',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  },
  {
    to: '/blog', label: 'Blog',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>,
  },
];

const APP_LINKS = [
  {
    to: '/capsuleList', label: 'My Capsules',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12M10 12a1 1 0 102 0 1 1 0 000-2m4 2a1 1 0 102 0 1 1 0 000-2" /></svg>,
  },
  {
    to: '/contacts', label: 'Contacts',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  },
  {
    to: '/integrations', label: 'Integrations',
    icon: <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
  },
];
const PLAN_BADGE = {
  legacy: { label: '👑 Legacy', className: 'text-teal-600 bg-teal-50' },
  starter: { label: '⭐ Starter', className: 'text-violet-600 bg-violet-50' },
};

const Header = () => {
  const { user, plan, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close user dropdown on outside click or Escape key
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [userMenuOpen]);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const badge = PLAN_BADGE[plan] || null;

  const navLinkClass = (to) =>
    `inline-flex items-center gap-1.5 px-3.5 py-2 text-[0.9rem] rounded-lg font-medium border whitespace-nowrap transition-colors ${
      location.pathname === to
        ? 'text-teal-700 bg-teal-50 border-teal-200'
        : 'text-stone-600 border-stone-200 hover:text-teal-700 hover:bg-teal-50 hover:border-teal-200'
    }`;

  const navLinkProps = (to) => ({
    className: navLinkClass(to),
    'aria-current': location.pathname === to ? 'page' : undefined,
  });

  const userInitial = (user?.displayName || user?.email || '?')[0].toUpperCase();

  return (
    <>
      {/* Backdrop — closes mobile menu on outside tap */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <header className="sticky top-0 z-50">
      <nav
        className={`w-full bg-white/95 backdrop-blur-md border-b transition-all duration-200 ${
          scrolled ? 'border-stone-200 shadow-sm' : 'border-stone-100'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 max-w-7xl mx-auto">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="LegaxyVault home">
            <img src={logoSrc} alt="LegaxyVault" className="h-8 w-auto object-contain" width="32" height="32" fetchPriority="high" />
            <span className="text-base font-semibold text-stone-800 tracking-tight hidden sm:block">LegaxyVault</span>
          </Link>

          {/* Desktop: public nav links (centre) */}
          <div className="hidden md:flex items-center gap-1.5 mx-6">
            {PUBLIC_LINKS.filter(({ to }) => !(user && to === '/pricing')).map(({ to, label, icon }) => (
              <Link key={to} to={to} {...navLinkProps(to)}>{icon}{label}</Link>
            ))}
          </div>

          {/* Desktop: right side */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                {/* App links */}
                {APP_LINKS.map(({ to, label, icon }) => (
                  <Link key={to} to={to} {...navLinkProps(to)}>{icon}{label}</Link>
                ))}

                {/* Divider */}
                <div className="w-px h-5 bg-stone-200 mx-1" />

                {/* User dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-stone-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                    aria-label="User menu"
                    aria-expanded={userMenuOpen}
                  >
                    {user.photoURL ? (
                      <img
                        src={user.photoURL.replace(/^http:/, 'https:')}
                        alt={user.displayName || 'User'}
                        className="w-7 h-7 rounded-full object-cover ring-1 ring-stone-200"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">
                        {userInitial}
                      </div>
                    )}
                    <div className="flex flex-col items-start leading-none gap-0.5">
                      <span className="text-sm font-medium text-stone-700 max-w-[120px] truncate">
                        {user.displayName || user.email?.split('@')[0]}
                      </span>
                      {badge && (
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                    <svg className={`w-4 h-4 text-stone-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown panel */}
                  {userMenuOpen && (
                    <div role="menu" aria-label="User menu" className="absolute right-0 mt-2 w-44 bg-white border border-stone-200 rounded-xl shadow-lg py-1 text-sm">
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-stone-700 hover:bg-stone-50"
                      >
                        <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile & Settings
                      </Link>
                      <div className="border-t border-stone-100 my-1" />
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-stone-600 hover:bg-stone-50 hover:text-red-600"
                      >
                        <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="px-4 py-1.5 text-sm font-medium text-stone-600 hover:text-stone-900 rounded-md hover:bg-stone-100 transition-colors">
                  Sign In
                </Link>
                <Link to="/signup" className="px-4 py-1.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors shadow-sm">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-stone-100 bg-white px-4 pb-4 pt-2 space-y-1 max-h-[calc(100dvh-4rem)] overflow-y-auto">
            {/* User identity */}
            {user && (
              <div className="flex items-center gap-2 px-3 py-2 mb-1 border-b border-stone-100 pb-3">
                {user.photoURL ? (
                  <img src={user.photoURL.replace(/^http:/, 'https:')} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">{userInitial}</div>
                )}
                <span className="text-sm font-medium text-stone-800 truncate">{user.displayName || user.email?.split('@')[0]}</span>
                {badge && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ml-auto ${badge.className}`}>{badge.label}</span>
                )}
              </div>
            )}

            {/* App links (logged-in only) */}
            {user && APP_LINKS.map(({ to, label, icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
                  location.pathname === to ? 'text-teal-700 bg-teal-50 border-teal-200' : 'text-stone-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
                }`}
              >
                {icon}{label}
              </Link>
            ))}

            {/* Divider before public links */}
            {user && <div className="border-t border-stone-100 my-1" />}

            {/* Public links */}
            {PUBLIC_LINKS.map(({ to, label, icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
                  location.pathname === to ? 'text-teal-700 bg-teal-50 border-teal-200' : 'text-stone-600 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
                }`}
              >
                {icon}{label}
              </Link>
            ))}

            {/* Auth actions */}
            <div className="border-t border-stone-100 pt-2 mt-1 space-y-1">
              {user ? (
                <>
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100">
                    Profile & Settings
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100">
                    Sign In
                  </Link>
                  <Link to="/signup" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 text-center transition-colors">
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
    </>
  );
}

export default Header;
